import React from 'react';
import ErrorBoundary from './error_boundary';
import { CommonsCmsDynamicPage, reverb_search_ListingsSearchRequest_PriceValue, reverb_search_ListingsSearchRequest_Sort } from '../rql';
import { COMPONENT_OUTPUT } from '../cms/component_output';
import * as COMPONENT_TYPES from '../cms/component_types';
import { useExpEnabled, useUser } from '../user_hooks';
import { inMobileApp } from '../user_context_helpers';
import experiments from '../experiments';
import { DEALS_AND_STEALS_CURATED_SET_ID } from '../constants';
import { MParticleEventName } from '../elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';

interface IProps {
  component: CommonsCmsDynamicPage.Components;
  canSsr: boolean;
  layoutSlug: string;
  position: number;
  cmsPageType?: string;
  cmsPageSlug?: string;
}

const GOOD_AND_ABOVE = ['new', 'excellent', 'b-stock', 'mint', 'excellent', 'very-good', 'good'];
const MAX_SSR_COMPONENT_POSITION = 3;

export default function CmsPageComponent({ component, canSsr, layoutSlug, position, cmsPageType, cmsPageSlug }: IProps) {
  const user = useUser();
  const dealsExperiment = useExpEnabled(experiments.DEALS_AND_STEALS_ROWS_V2);

  const ComponentClass = COMPONENT_OUTPUT[component.componentType];
  const props = JSON.parse(component.data) || {};

  if (component.componentType === COMPONENT_TYPES.LISTINGS_COLLECTION) {
    props.userShippingRegion = user.shippingRegionCode;

    const eligible = props.curatedSetId === DEALS_AND_STEALS_CURATED_SET_ID &&
      user.shippingRegionCode === 'US_CON' &&
      (cmsPageType === 'category' || (cmsPageType === 'promo' && cmsPageSlug === 'deals-and-steals'));
    if (eligible) {
      props.mParticleView = {
        componentName: 'ExperimentalDealsAndStealsRow',
        eventName: MParticleEventName.ComponentView,
        experiments: formatExperimentsForEventAttributes(user, [
          experiments.DEALS_AND_STEALS_ROWS_V2,
        ]),
      };
    }

    const usePricingData = eligible && dealsExperiment;
    if (usePricingData) {
      props.curatedSetId = undefined;
      props.dealsOrWellPriced = true;
      props.conditionSlugs = GOOD_AND_ABOVE;
      props.sort = reverb_search_ListingsSearchRequest_Sort.PUBLISHED_AT_DESC;
      props.priceValueV2 = reverb_search_ListingsSearchRequest_PriceValue.GOOD;
      props.shipsToMe = true;
    }
  }

  if (component.componentType === COMPONENT_TYPES.RECENTLY_VIEWED_LISTINGS) {
    props.loggedOut = user.loggedOut;
  }

  props.layoutSection = component.layoutSection;
  props.pagePosition = position;
  props.ssr = (canSsr && position < MAX_SSR_COMPONENT_POSITION);
  props.inMobileApp = inMobileApp(user);
  props.layoutSlug = layoutSlug;

  return (
    <ErrorBoundary
      errorName="cms-component-error"
      errorData={{
        component_type: component.componentType,
        component_id: component.id,
      }}
    >
      <ComponentClass {...props} />
    </ErrorBoundary>
  );
}
